/**
 * Flow:
 * 1. Call the API to get the similar projects (using the project_id)
 * 2. Display the similar projects in a table (API returns the 10 most similar projects)
 * 3. Display the project we are comparing to in a card
 * 
 * TODO:
 *  - Maybe add pagination to only up to 50 total projects?
 */

import { useEffect } from "react";
import { useState } from "react";
import { SimilarProjectInterface } from "../Interface/SimilarProjectInterface";
import { ProjectProps } from "@/interfaces/project_types";
import { getSimilarProjects } from "../api/similar_projects";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
  } from "@/components/ui/tooltip"
import { InfoIcon } from "lucide-react";
import { TitleHeaderHyperlink } from "@/components/Dashboard/UI/Cards/Utils/TitleHeaderHyperlink";

export function SimilarProjects({ project }: { project: ProjectProps }) {
    const [_similarProjects, setSimilarProjects] = useState<SimilarProjectInterface[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setIsLoading(true)
        if (project.project_id) {
            getSimilarProjects(project.project_id).then((projects) => {
                setSimilarProjects(projects)
                setIsLoading(false)
            })
        }
        
    }, [project.project_id])
    
    return (
        <div className="flex flex-col gap-5 bg-stone-50 dark:bg-stone-900">
            <Header />

            {isLoading ? <Loading /> : _similarProjects.length === 0 ? <NoSimilarProjects /> : <SimilarProjectTable similar_projects={_similarProjects} project_to_compare={project} />}
        </div>
    )
}

function Loading() {
    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold">(Loading...)</h3>
        </div>
    )
}
function Header() {
    return (
        <div className="flex flex-row gap-3">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="flex flex-row gap-2 dark:text-white">
                            <h3>Projects for Reference:</h3>
                            <InfoIcon className="w-4 h-4" />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>
                            Projects that are similar to the current project you're on.
                            <br />
                            <br />
                            The similarity is based on the project name, client name, city, size, and project type.
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    )
}

function NoSimilarProjects() {
    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold">(No similar projects found)</h3>
            <p className="text-gray-500">
                (This project is either unique or missing attributes that are used to determine similarity.)
            </p>
        </div>
    )
}

// This is the component that displays the similar projects in a table
function SimilarProjectTable({ similar_projects }: { similar_projects: SimilarProjectInterface[], project_to_compare: ProjectProps }) {
    return (
        <div className="grid grid-cols-5 gap-5 pb-6 ">
            {similar_projects.map((project) => (
            <SimilarProjectComponent key={project.project_id} project={project} />
            ))}
        </div>
    )
}

// This is the component that displays a single similar project
function SimilarProjectComponent({ project }: { project: SimilarProjectInterface }) {
    return (
        <div className="flex flex-row gap-4 p-5 border-t-2 border-orange-700 rounded-lg shadow-lg min-w-60 bg-gray-50 dark:bg-stone-900">
            <div className="flex flex-col w-full gap-2">
                <TitleHeaderHyperlink title={project.project_name} folder_location={project.folder_location ?? ""} />
                
                <p className="text-sm text-gray-600 dark:text-gray-400">Client: {project.client_name || <span className="italic text-gray-500">(No client name)</span>}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">City: {project.city || <span className="italic text-gray-500">(No city)</span>}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Type: {project.template || <span className="italic text-gray-500">(No template)</span>}</p>
            </div>
        </div>
    )
}